import { useState } from "react";
import Btn from "./ui/Btn.jsx";
import Display from "./ui/Display.jsx";
import Eyebrow from "./ui/Eyebrow.jsx";
import Ornament from "./ui/Ornament.jsx";
import { signInWithMicrosoft, signOut, userEmail } from "../lib/auth.js";

// Microsoft's four-square logo (official brand colours).
function MicrosoftMark({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 23 23" aria-hidden="true">
      <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
      <rect x="12" y="1"  width="10" height="10" fill="#7FBA00" />
      <rect x="1"  y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export default function SignIn({ notAllowedEmail = null, session = null }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleSignIn = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await signInWithMicrosoft();
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    setBusy(true);
    try { await signOut(); } finally { setBusy(false); }
  };

  const deniedEmail = notAllowedEmail || userEmail(session);

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--paper-0)",
      color: "var(--ink)",
      fontFamily: "var(--ui)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "48px 24px",
    }}>
      <div style={{ width: "100%", maxWidth: 440, textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginBottom: 28 }}>
          <img src="/atrum-logo-white.png" className="brand-logo" alt="Atrum" style={{ width: 28, height: 30, objectFit: "contain" }} />
          <div style={{ width: 1, height: 22, background: "var(--line-2)" }} />
          <div style={{ textAlign: "left" }}>
            <div style={{ fontFamily: "var(--display)", fontSize: 18, fontStyle: "italic", letterSpacing: 0.2, lineHeight: 1 }}>
              Placement
            </div>
            <div style={{ fontSize: 10.5, color: "var(--ink-3)", letterSpacing: 1.6, textTransform: "uppercase", fontWeight: 600, marginTop: 3 }}>
              by Atrum
            </div>
          </div>
        </div>

        <Eyebrow style={{ marginBottom: 14 }}>
          {notAllowedEmail ? "Access denied" : "Sign in"}
        </Eyebrow>

        <Display size={40} style={{ marginBottom: 18 }}>
          {notAllowedEmail ? (
            <>That account isn't<br /><span style={{ fontStyle: "italic", color: "var(--ink-2)" }}>on the list.</span></>
          ) : (
            <>A quiet place<br /><span style={{ fontStyle: "italic", color: "var(--ink-2)" }}>for hosting.</span></>
          )}
        </Display>

        <div style={{ color: "var(--ink-2)", fontSize: 14, lineHeight: 1.55, marginBottom: 28 }}>
          {notAllowedEmail ? (
            <>
              <code style={{ fontFamily: "var(--ui)", color: "var(--ink)" }}>{deniedEmail}</code> isn't authorised to
              use Placement. If that's a mistake, ask an admin to add you.
            </>
          ) : (
            <>Placement is an internal tool. Sign in with the Microsoft account you were invited with.</>
          )}
        </div>

        <Ornament style={{ marginBottom: 28 }} />

        {error && (
          <div style={{
            padding: "10px 14px",
            borderRadius: 10,
            background: "var(--warn-soft)",
            border: "1px solid var(--warn-line)",
            color: "var(--warn-ink)",
            fontSize: 13,
            marginBottom: 18,
            textAlign: "left",
          }}>{error}</div>
        )}

        {notAllowedEmail ? (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <Btn variant="primary" size="md" onClick={handleSignOut} disabled={busy}>
              {busy ? "Signing out…" : "Sign out"}
            </Btn>
            <Btn variant="ghost" size="md" onClick={handleSignIn} disabled={busy}>
              Try another account
            </Btn>
          </div>
        ) : (
          <Btn variant="primary" size="lg" onClick={handleSignIn} disabled={busy}
               style={{ paddingLeft: 18, paddingRight: 20 }}>
            <MicrosoftMark size={16} />
            {busy ? "Redirecting…" : "Continue with Microsoft"}
          </Btn>
        )}

        <div style={{ marginTop: 24, fontSize: 11.5, color: "var(--ink-3)", letterSpacing: 0.3 }}>
          Use your company Microsoft account.
        </div>
      </div>
    </div>
  );
}
